/* ===========================
   cart-hard-sync.js (FULL)
   Dawn 15.2 Cart UI Hard Sync
   - Ensures cart bubble + estimated total stay accurate after cart mutations
   - Cart-only, no fetch override
   - Debounced, Safari-hardened triggers
   - Self-healing retry for transient header DOM (prevents unnecessary reloads)
   - Optional debug: add ?cartdebug=1 to /cart
   =========================== */

(function () {
  if (window.__CART_HARD_SYNC__) return;
  window.__CART_HARD_SYNC__ = true;

  /* --- DEBUG TOGGLE ---
     Visit /cart?cartdebug=1 to enable console logs
  */
  const DEBUG = new URLSearchParams(window.location.search).has("cartdebug");
  function log(...args) {
    if (DEBUG) console.log("[CART-HARD-SYNC]", ...args);
  }

  function formatMoney(cents) {
    try {
      if (window.Shopify && typeof Shopify.formatMoney === "function") {
        return Shopify.formatMoney(cents);
      }
    } catch (e) {}
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  function updateBubble(itemCount) {
    const bubble = document.getElementById("cart-icon-bubble");
    if (!bubble) return false;

    let updated = false;

    // Update any obvious numeric spans
    bubble.querySelectorAll("span").forEach((s) => {
      if (/^\s*\d+\s*$/.test(s.textContent || "")) {
        s.textContent = String(itemCount);
        updated = true;
      }
    });

    // Common Dawn markup: .cart-count-bubble > span
    const countBubble = bubble.querySelector(".cart-count-bubble");
    if (countBubble) {
      const num = countBubble.querySelector("span");
      if (num) {
        num.textContent = String(itemCount);
        updated = true;
      }

      // Accessibility text (often visually hidden)
      const sr = countBubble.querySelector(".visually-hidden");
      if (sr) {
        const current = sr.textContent || "";
        if (/\d+/.test(current)) {
          sr.textContent = current.replace(/\d+/, String(itemCount));
        } else {
          sr.textContent = `${current} ${itemCount}`.trim();
        }
        updated = true;
      }

      // Show/hide bubble if theme expects it
      if (itemCount > 0) {
        countBubble.classList.remove("hidden");
      } else {
        countBubble.classList.add("hidden");
      }
    }

    return updated;
  }

  function updateEstimatedTotal(totalCents) {
    const footer = document.getElementById("main-cart-footer");
    if (!footer) return false;

    // Dawn cart footer total value is usually .totals__total-value
    const totalEl =
      footer.querySelector(".totals__total-value") ||
      footer.querySelector("[data-cart-total]") ||
      footer.querySelector("[data-estimated-total]");

    if (!totalEl) return false;

    totalEl.textContent = formatMoney(totalCents);
    return true;
  }

  async function hardSync() {
    try {
      log("fetching /cart.js …");

      const res = await fetch(`${routes.cart_url}.js`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });

      log("fetch complete", res.status);

      if (!res.ok) return;

      const cart = await res.json();
      log("cart.js parsed", { item_count: cart.item_count, total_price: cart.total_price });

      const okBubble = updateBubble(cart.item_count);
      const okTotal = updateEstimatedTotal(cart.total_price);
      log("dom updates", { okBubble, okTotal });

      // Self-heal: header bubble can be transiently missing due to header/cart-drawer scripts
      if (!okBubble || !okTotal) {
        log("dom incomplete — retrying…", { okBubble, okTotal });

        let attempts = 0;
        const maxAttempts = 3;

        const retry = () => {
          attempts++;

          const retryBubble = updateBubble(cart.item_count);
          const retryTotal = updateEstimatedTotal(cart.total_price);

          log("retry attempt", attempts, { retryBubble, retryTotal });

          if (retryBubble && retryTotal) return;

          if (attempts < maxAttempts) {
            setTimeout(retry, 120);
          } else {
            log("final fallback reload");
            if (!window.__CART_HARD_SYNC_RELOADING__) {
              window.__CART_HARD_SYNC_RELOADING__ = true;
              window.location.reload();
            }
          }
        };

        setTimeout(retry, 120);
      }
    } catch (e) {
      log("hardSync error", e);
      if (!window.__CART_HARD_SYNC_RELOADING__) {
        window.__CART_HARD_SYNC_RELOADING__ = true;
        window.location.reload();
      }
    }
  }

  // Debounce to prevent spamming when clicking +/- quickly
  let t = null;
  function scheduleHardSync(reason) {
    clearTimeout(t);
    log("scheduleHardSync()", reason || "");
    t = setTimeout(() => {
      log("hardSync() executing now");
      hardSync();
    }, 200);
  }

  function isCartPage() {
    // routes.cart_url is "/cart" in Dawn
    return window.location.pathname === routes.cart_url;
  }

  document.addEventListener("DOMContentLoaded", function () {
    if (!isCartPage()) return;

    // If Dawn pub/sub exists and fires, use it (more “native” timing)
    try {
      if (typeof subscribe === "function" && window.PUB_SUB_EVENTS?.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
          scheduleHardSync("PUB_SUB_EVENTS.cartUpdate");
        });
      }
    } catch (e) {
      log("pub/sub subscribe not available", e);
    }

    // Click triggers (capture so we run even if another script stops propagation)
    document.addEventListener(
      "click",
      function (e) {
        const el = e.target;

        const isQtyBtn = el.closest('button[name="plus"], button[name="minus"]');
        const isRemoveBtn =
          el.closest("cart-remove-button") ||
          el.closest('button[aria-label*="Remove"]') ||
          el.closest('a[href^="/cart/change"]') ||
          el.closest('button[name="remove"]');

        if (isQtyBtn || isRemoveBtn) {
          log("mutation click detected", { isQtyBtn: !!isQtyBtn, isRemoveBtn: !!isRemoveBtn });
          scheduleHardSync("click");
        }
      },
      true
    );

    // Safari/Firefox edge cases: sometimes click is missed—listen to pointerup too
    document.addEventListener(
      "pointerup",
      function (e) {
        const el = e.target;

        const isRemoveBtn =
          el.closest("cart-remove-button") ||
          el.closest('button[aria-label*="Remove"]') ||
          el.closest('a[href^="/cart/change"]') ||
          el.closest('button[name="remove"]');

        if (isRemoveBtn) {
          log("mutation pointerup detected");
          scheduleHardSync("pointerup");
        }
      },
      true
    );

    // Qty input changes
    document.addEventListener(
      "change",
      function (e) {
        const input = e.target.closest('input[name="updates[]"], input.quantity__input');
        if (input) {
          log("mutation change detected");
          scheduleHardSync("change");
        }
      },
      true
    );

    // Form submit fallback (covers non-AJAX or edge cases)
    document.addEventListener(
      "submit",
      function (e) {
        const form = e.target;
        if (form && form.matches && form.matches('form[action^="/cart"]')) {
          log("cart form submit detected");
          scheduleHardSync("submit");
        }
      },
      true
    );

    log("installed on cart page");
        /* --- CHECKOUT FAILSAFE (Cart page) ---
       Some browsers/extensions block the POST /cart checkout submit flow.
       If the customer clicks CHECKOUT, go straight to /checkout.
       Safe on Shopify and avoids “flash/no navigation” issues.
    */
    (function attachCheckoutFailsafe() {
      const btn = document.getElementById('checkout');
      if (!btn) return;

      btn.addEventListener(
        'click',
        function (e) {
          // Respect disabled state (empty cart)
          if (btn.disabled) return;

          // Force checkout navigation (bypass POST submit issues)
          e.preventDefault();

          const checkoutUrl =
            (window.routes && routes.checkout_url) ? routes.checkout_url : '/checkout';

          window.location.assign(checkoutUrl);
        },
        true
      );
    })();

  });
})();
